import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { v2, parseApiErrorBody } from '@/api/v2Client';
import type {
  DocumentValidationResultsResponse,
  LinkValidationDetails,
  ValidationResultEntry,
} from '@/api/v2Client';

/**
 * Wave E.4 — Inspector "Links" tab.
 *
 * Fetches the latest validation results for the current document, filters
 * to the link validator (WAVE-E4-LINK-01), and renders a friendly summary
 * + a list of any broken hyperlinks. "Re-validate now" button kicks a
 * fresh run on the WHOLE submission (the link validator runs per-document
 * via the orchestrator) and then refetches.
 */

export interface LinksTabProps {
  readonly documentId: string;
  readonly submissionId: string;
  /** When false (read-only role) the re-validate button is disabled. */
  readonly canRevalidate: boolean;
  /**
   * Phase 5.X-screen-review-part-c — when true (Submissions:update or
   * operator role), show the "Test Inject" affordance.
   *
   * navigation-foundation (U01) — the inject PREVIEW backend is still a stub
   * (returns ok:false / no preview), so the affordance is rendered visibly
   * UNAVAILABLE (a disabled control with an explanatory note) rather than as a
   * clickable success. It must not imply a working feature.
   */
  readonly canTestInject?: boolean;
  /**
   * phase-compiler-six-tabs — "Re-validate now" kicks a WHOLE-submission run,
   * so the submission-level results the Validation tab and the chrome summary
   * read go stale until their own poll. The parent passes this to refresh those
   * parent results immediately after a successful re-validate.
   */
  readonly onRevalidated?: () => void;
}

const LINK_RULE_ID = 'WAVE-E4-LINK-01';

const sectionStyle: CSSProperties = {
  padding: 12,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
};

const placeholderStyle: CSSProperties = {
  padding: '16px 12px',
  fontSize: 13,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  padding: '16px 12px',
  fontSize: 13,
  color: 'var(--danger, #b91c1c)',
};

const passLineStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--ok, #15803d)',
  marginBottom: 8,
};

const failLineStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--danger, #b91c1c)',
  marginBottom: 8,
};

const brokenLinkRowStyle: CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface2)',
  marginBottom: 6,
  fontSize: 13,
};

const brokenLinkTextStyle: CSSProperties = {
  fontWeight: 600,
  color: 'var(--text)',
};

const brokenLinkMetaStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  marginTop: 2,
};

const buttonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 8,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
};

function findLinkResult(
  data: DocumentValidationResultsResponse | null,
): ValidationResultEntry | null {
  if (!data) return null;
  return data.results.find((r) => r.ruleName === LINK_RULE_ID) ?? null;
}

export function LinksTab({
  documentId,
  submissionId,
  canRevalidate,
  canTestInject = false,
  onRevalidated,
}: LinksTabProps): ReactElement {
  const [data, setData] = useState<DocumentValidationResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revalidating, setRevalidating] = useState(false);

  const fetchResults = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await v2.documents.getValidationResults(documentId);
      setData(next);
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void fetchResults();
  }, [fetchResults]);

  const handleRevalidate = useCallback(async (): Promise<void> => {
    if (!canRevalidate) return;
    setRevalidating(true);
    setError(null);
    try {
      await v2.submissions.validateNow(submissionId);
      await fetchResults();
      // The run is submission-wide — let the parent refresh its own
      // submission-level results (chrome summary, Validation submission scope).
      onRevalidated?.();
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setRevalidating(false);
    }
  }, [canRevalidate, submissionId, fetchResults, onRevalidated]);

  const linkResult = findLinkResult(data);

  // Renderable states ----------------------------------------------------------
  if (loading && !data) {
    return (
      <div data-testid="links-tab-loading" style={placeholderStyle}>
        Loading link validation results…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div data-testid="links-tab-error" style={errorStyle} role="alert">
        Could not load link validation: {error}
      </div>
    );
  }
  if (!linkResult) {
    return (
      <div data-testid="links-tab" style={sectionStyle}>
        <div data-testid="links-tab-no-results" style={placeholderStyle}>
          Link validation hasn’t been run for this document yet.
        </div>
        <button
          type="button"
          data-testid="links-tab-revalidate"
          onClick={() => {
            void handleRevalidate();
          }}
          disabled={!canRevalidate || revalidating}
          style={!canRevalidate || revalidating ? buttonDisabledStyle : buttonStyle}
        >
          {revalidating ? 'Re-validating…' : 'Re-validate now'}
        </button>
      </div>
    );
  }

  const details = (linkResult.details as LinkValidationDetails | null) ?? null;
  // navigation-foundation (U03) — a historical/legacy/malformed `details` must
  // NOT have its missing values defaulted into invented facts ("0 broken",
  // "N of N resolved", "No hyperlinks found"). Probe the ACTUAL shape and only
  // derive counts from fields that are genuinely present; otherwise fall back
  // to the backend message and label the coverage unknown.
  const hasBrokenArray = details != null && Array.isArray(details.brokenLinks);
  const brokenLinks = hasBrokenArray ? details!.brokenLinks : [];
  const brokenCount = brokenLinks.length;
  const isCount = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0;
  const hasTotal = isCount(details?.totalLinks);
  const totalLinks = hasTotal ? (details!.totalLinks as number) : 0;
  const externalLinks =
    details != null && typeof details.externalLinks === 'number' ? details.externalLinks : 0;
  const remoteUnverified =
    details != null && typeof details.remoteUnverifiedLinks === 'number'
      ? details.remoteUnverifiedLinks
      : 0;
  const incompleteLinks =
    details != null && typeof details.incompleteLinks === 'number' ? details.incompleteLinks : 0;
  const scanIncomplete = details != null && details.scanIncomplete === true;
  // A historical pass came from the older evaluator and cannot establish
  // today's coverage. Require the actual coverage fields before deriving a
  // resolved count; Word checks instead carry their explicit authority.
  const countsKnown = hasTotal && hasBrokenArray &&
    isCount(details?.externalLinks) && isCount(details?.remoteUnverifiedLinks) &&
    isCount(details?.incompleteLinks) &&
    (details?.method === 'docx'
      ? details.check === 'authoritative' || details.check === 'advisory'
      : typeof details?.scanIncomplete === 'boolean') &&
    externalLinks + remoteUnverified + incompleteLinks + brokenCount <= totalLinks;
  // "resolved" is DERIVED, never "all totalLinks": external (out of scope),
  // remote (other-file, unverifiable), cycle/budget incompletes and broken
  // links are NOT passes.
  const resolved = Math.max(
    0,
    totalLinks - externalLinks - remoteUnverified - incompleteLinks - brokenCount,
  );

  const isWordNative = details?.method === 'docx';
  const isAdvisory = details?.check === 'advisory';
  const isAuthoritative = details?.check === 'authoritative';
  // A skip carrying broken findings must still SHOW them (U03): both a tagged
  // advisory skip AND a legacy skip that predates the `check` field. A skip
  // without findings stays the generic skip line.
  const isAdvisorySkip = linkResult.status === 'skip' && (isAdvisory || brokenCount > 0);
  const showBrokenList =
    brokenCount > 0 && (linkResult.status === 'fail' || isAdvisorySkip);

  // Honest coverage breakdown: the counts the backend reports but are NOT
  // passes, surfaced so the summary never overstates what was verified. Shown
  // on fail/advisory as well as pass (U03 finding 4). Absent fields stay 0 and
  // therefore add no note — that is honest silence, not an invented zero claim.
  const coverageNotes: string[] = [];
  if (externalLinks > 0) coverageNotes.push(`${externalLinks} external (not checked)`);
  if (remoteUnverified > 0) coverageNotes.push(`${remoteUnverified} remote/other-file (not verified)`);
  if (incompleteLinks > 0) coverageNotes.push(`${incompleteLinks} could not be fully resolved`);
  if (scanIncomplete) coverageNotes.push('the link scan did not finish');

  return (
    <div data-testid="links-tab" style={sectionStyle}>
      {/* phase-region-format-rules-and-docx-links — Word-native results are
          labelled: authoritative only when the backend confirms the .docx IS
          the artifact for the slot (check==='authoritative'); advisory when it
          ran against the Word source and the final artifact was not verified.
          When the backend did not tag the check, stay neutral ("Word-native
          check") rather than asserting either direction. Two results are never
          presented as one truth. */}
      {isWordNative && (
        <div
          data-testid="links-tab-docx-label"
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            marginBottom: 8,
            padding: '6px 8px',
            background: 'var(--surface2)',
            borderRadius: 4,
          }}
        >
          <strong>
            {isAdvisory
              ? 'Word source check (advisory)'
              : isAuthoritative
                ? 'Word-native check (authoritative)'
                : 'Word-native check'}
          </strong>
          {isAdvisory && ' — run against the Word source; the final artifact has not been verified.'}
          {details?.caveat ? ` ${details.caveat}` : ''}
        </div>
      )}
      {linkResult.status === 'pass' && (
        countsKnown ? (
          <div data-testid="links-tab-pass-line" style={passLineStyle}>
            ✅ {totalLinks === 0
              ? 'No hyperlinks found in this document.'
              : `${resolved} of ${totalLinks} hyperlink${totalLinks === 1 ? '' : 's'} resolved.`}
            {coverageNotes.length > 0 && (
              <span data-testid="links-tab-coverage-note" style={{ color: 'var(--text-muted)' }}>
                {' '}({coverageNotes.join('; ')})
              </span>
            )}
          </div>
        ) : (
          // U03 finding 1 — details missing/legacy shape: do NOT invent
          // "0 of 0" or "no hyperlinks". Surface the backend message and say
          // the coverage is unknown.
          <div data-testid="links-tab-pass-unknown" style={placeholderStyle}>
            ℹ️ Saved result: {linkResult.message ?? 'Passed.'}
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}(hyperlink coverage details are not available)
            </span>
          </div>
        )
      )}
      {(linkResult.status === 'fail' || isAdvisorySkip) && (
        <>
          <div
            data-testid={linkResult.status === 'fail' ? 'links-tab-fail-line' : 'links-tab-advisory-line'}
            style={linkResult.status === 'fail' ? failLineStyle : { ...failLineStyle, color: 'var(--text-muted)' }}
          >
            {linkResult.status === 'fail' ? '❌' : 'ℹ️'}{' '}
            {!hasBrokenArray
              ? /* U03 finding 2 — a failure with no broken-link list must
                   show the failure, never "0 broken". */
                `${linkResult.message ?? 'Link validation failed.'} (broken-link details are not available)`
              : isAdvisorySkip && brokenCount === 0
                ? 'Word source check (advisory) found no broken hyperlinks in the Word source.'
                : `${brokenCount} broken hyperlink${brokenCount === 1 ? '' : 's'} found`}
            {hasBrokenArray && isAdvisorySkip && brokenCount > 0
              ? ' in the Word source (advisory check)'
              : ''}
          </div>
          {coverageNotes.length > 0 && (
            <div
              data-testid="links-tab-coverage-note"
              style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}
            >
              Coverage: {coverageNotes.join('; ')}.
            </div>
          )}
          {showBrokenList && (
            <div data-testid="links-tab-broken-list">
              {brokenLinks.map((bl, idx) => (
                <div
                  key={`${bl.text}-${idx}`}
                  data-testid={`links-tab-broken-${idx}`}
                  style={brokenLinkRowStyle}
                >
                  <div style={brokenLinkTextStyle}>{bl.text || '(unnamed link)'}</div>
                  <div style={brokenLinkMetaStyle}>
                    {isWordNative ? 'Document' : `Page ${bl.page}`} → {bl.reason}
                    {' · '}
                    <code style={{ fontSize: 11 }}>{bl.targetType}</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {linkResult.status === 'skip' && !isAdvisorySkip && (
        <div data-testid="links-tab-skip-line" style={placeholderStyle}>
          Link validation not fully completed: {linkResult.message ?? 'unknown reason'}
        </div>
      )}
      {error && (
        <div role="alert" style={{ ...errorStyle, marginTop: 8 }}>
          {error}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        Last validated: {new Date(linkResult.validatedAt).toLocaleString()}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="links-tab-revalidate"
          onClick={() => {
            void handleRevalidate();
          }}
          disabled={!canRevalidate || revalidating}
          style={!canRevalidate || revalidating ? buttonDisabledStyle : buttonStyle}
        >
          {revalidating ? 'Re-validating…' : 'Re-validate now'}
        </button>
        {canTestInject && (
          // navigation-foundation (U01) — the inject-preview backend is a stub,
          // so this affordance is visibly UNAVAILABLE: a disabled control, not a
          // clickable success. It must not imply a working feature exists.
          <button
            type="button"
            data-testid="links-tab-test-inject"
            disabled
            aria-disabled="true"
            style={buttonDisabledStyle}
            title="Link-injection preview is not available yet"
          >
            Test Inject (unavailable)
          </button>
        )}
      </div>
      {canTestInject && (
        <div
          data-testid="links-tab-inject-unavailable"
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: '8px 10px',
            borderRadius: 4,
            background: 'var(--surface2)',
            color: 'var(--text-muted)',
          }}
        >
          Link-injection preview is not available yet. The publish-time pass
          restyles existing cross-document links; it does not generate new ones.
        </div>
      )}
    </div>
  );
}
